import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {BufferConditionKeys, MeltingTempPreferences, NucleotideType} from 'biojs/BioConstants';
import {AttachPrimerOptions} from 'constants/AttachPrimerConstants';
import {MinimalFolder} from 'constants/FolderConstants';
import {OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE} from 'constants/ModelTypeConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import MultipleFolderSelector from 'file-browser/MultipleFolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import NucleotideTypeSelector from 'molbio/find-primers/NucleotideTypeSelector';
import {MELTING_TEMP_DEFAULT_ERROR_STATE} from 'molbio/melting-temp/MeltingTempUtils';
import MisprimeParams from 'molbio/misprime/MisprimeParams';
import {
  getMisprimeDefaultsFromPreferences,
  MisprimeOptions,
  setMisprimePreferences,
} from 'molbio/misprime/MisprimeUtils';
import preferences from 'Preferences';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {ButtonSize} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

import MeltingTempParams from './melting-temp/MeltingTempParams';

// We want to support a list of default folders, but also to take advantage of the fallback to the currently
// open folder that is only implemented in FolderDefaults.get
function defaultFolderIds(): Array<string> {
  const defaultList = FolderDefaults.getList(FolderDefaultsKey.FIND_OLIGO);
  if (defaultList) {
    return defaultList;
  }
  const defaultSingle = FolderDefaults.get(FolderDefaultsKey.FIND_OLIGO);
  if (defaultSingle) {
    return [defaultSingle];
  }
  return [];
}

enum FindTypeKey {
  FOLDERS = 'FOLDERS',
  TAG_SCHEMA = 'TAG_SCHEMA',
}

export interface BulkAttachPrimersModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (options: AttachPrimerOptions) => void;
}

export default function BulkAttachPrimersModal({
  onRequestClose,
  onRequestSubmit,
}: BulkAttachPrimersModalProps): JSX.Element {
  const [findType, setFindType] = useState<FindTypeKey>(FindTypeKey.FOLDERS);
  const [tagSchemaId, setTagSchemaId] = useState<string>();
  const [folderIds, setFolderIdsState] = useState<Array<string>>(() => defaultFolderIds());
  function setFolderIds(newFolderIds: Array<string>): void {
    FolderDefaults.setList(FolderDefaultsKey.FIND_OLIGO, newFolderIds);
    setFolderIdsState(newFolderIds);
  }

  const [nucleotideType, setNucleotideType] = useState<NucleotideType>(NucleotideType.DNA);
  const [misprimeParams, setMisprimeParams] = useState<MisprimeOptions>(getMisprimeDefaultsFromPreferences());

  const [meltingTempOptionsDNA, setMeltingTempOptionsDNA] = useState<MeltingTempPreferences>(
    preferences.get(PrimerPreferences.MELTING_TEMP_DNA)
  );
  const [meltingTempOptionsRNA, setMeltingTempOptionsRNA] = useState<MeltingTempPreferences>(
    preferences.get(PrimerPreferences.MELTING_TEMP_RNA)
  );
  const [dnaOptionsHaveValidationError, setDNAOptionsHaveValidationError] = useState<{
    [key in BufferConditionKeys]: boolean;
  }>(MELTING_TEMP_DEFAULT_ERROR_STATE);
  const [rnaOptionsHaveValidationError, setRNAOptionsHaveValidationError] = useState<{
    [key in BufferConditionKeys]: boolean;
  }>(MELTING_TEMP_DEFAULT_ERROR_STATE);

  function handleRequestSubmit(): void {
    onRequestSubmit({
      folderIds: findType === FindTypeKey.FOLDERS ? folderIds : undefined,
      tagSchemaId: findType === FindTypeKey.TAG_SCHEMA ? tagSchemaId : undefined,
      unique_match: misprimeParams.uniqueMatch ?? false,
      exact_match: misprimeParams.exactMatch ?? false,
      min_matching_bases: misprimeParams.minMatchingBases!,
      min_temp: misprimeParams.minMeltingTemp!,
      max_temp: misprimeParams.maxMeltingTemp!,
      max_mismatches: misprimeParams.maxMismatches,
      max_total_mismatches: misprimeParams.maxTotalMismatches!,
      min_base_index: 0,
      max_base_index: 0,
      dnaTmPreferences: meltingTempOptionsDNA,
      rnaTmPreferences: meltingTempOptionsRNA,
      nucleotideType,
    });
    preferences.set({
      [PrimerPreferences.MELTING_TEMP_DNA]: meltingTempOptionsDNA,
      [PrimerPreferences.MELTING_TEMP_RNA]: meltingTempOptionsRNA,
    });
    setMisprimePreferences(misprimeParams);
  }

  const isSubmitDisabled =
    (findType === FindTypeKey.FOLDERS && folderIds.length === 0) ||
    (findType === FindTypeKey.TAG_SCHEMA && tagSchemaId == null) ||
    misprimeParams.minMatchingBases == null ||
    misprimeParams.minMeltingTemp == null ||
    misprimeParams.maxMeltingTemp == null ||
    misprimeParams.maxTotalMismatches == null ||
    Object.values(dnaOptionsHaveValidationError).some(
      (optionHasValidationError) => optionHasValidationError
    ) ||
    Object.values(rnaOptionsHaveValidationError).some((optionHasValidationError) => optionHasValidationError);

  return (
    <Modal
      buttons={[{text: 'Attach primers', submit: true, disabled: isSubmitDisabled}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleRequestSubmit}
      title="Attach Primers"
    >
      <h4 className={css(styles.noTopMargin)}>Mispriming</h4>
      <>
        <div className={css(styles.section)}>
          <div>Use oligos</div>
          <ToggleGroup
            onChangeSelection={(findTypeKey: FindTypeKey) => {
              setFindType(findTypeKey);
            }}
            selection={findType}
            isFullWidth={true}
            size={ButtonSize.SMALL}
            options={[
              {id: FindTypeKey.FOLDERS, buttonProps: {text: 'In folders'}},
              {id: FindTypeKey.TAG_SCHEMA, buttonProps: {text: 'With schema'}},
            ]}
          />
        </div>
        <div className={css(styles.section)}>
          {findType === FindTypeKey.FOLDERS && (
            <MultipleFolderSelector
              selectedFolderIds={folderIds}
              onSelectFolders={(folders: Array<MinimalFolder>) => {
                setFolderIds(folders.map((f) => f.api_identifier));
              }}
              showInventory={true}
            />
          )}
          {findType === FindTypeKey.TAG_SCHEMA && (
            <TagSchemaSelector
              allowNone={false}
              oligoAwareFolderItemTypes={[OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[nucleotideType]]}
              onChange={(tagSchema) => {
                setTagSchemaId(tagSchema?.id);
              }}
              selectedTagSchemaId={tagSchemaId}
              forceVisibleSelectedTagSchema={null}
            />
          )}
        </div>
      </>
      <div className={css(styles.section)}>
        <div className={css(styles.section)}>
          <NucleotideTypeSelector nucleotideType={nucleotideType} setNucleotideType={setNucleotideType} />
        </div>
        <MisprimeParams
          misprimeParams={misprimeParams}
          setMisprimeParams={setMisprimeParams}
          showExactAndUniqueMatch={true}
        />
      </div>
      <div className={css(styles.section)}>
        <h4>Melting Temperature</h4>
        <MeltingTempParams
          nucleotideType={nucleotideType}
          meltingTempOptions={
            nucleotideType === NucleotideType.DNA ? meltingTempOptionsDNA : meltingTempOptionsRNA
          }
          setMeltingTempOptions={
            nucleotideType === NucleotideType.DNA ? setMeltingTempOptionsDNA : setMeltingTempOptionsRNA
          }
          optionsHaveValidationError={
            nucleotideType === NucleotideType.DNA
              ? dnaOptionsHaveValidationError
              : rnaOptionsHaveValidationError
          }
          setOptionsHaveValidationError={
            nucleotideType === NucleotideType.DNA
              ? setDNAOptionsHaveValidationError
              : setRNAOptionsHaveValidationError
          }
        />
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: '8px',
  },
  noTopMargin: {
    marginTop: 0,
  },
});
